import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient } from "../../constants.ts";
import {
  Button,
  Collapsible,
  inlineCodeClass,
  inputClass,
  PageSpinner,
  TokenDisplay,
} from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";

const telegramTokenRegex = /^\d{6,}:[\w-]{35,}$/;

const validateToken = (token: string) => {
  if (!token) return null;
  return telegramTokenRegex.test(token)
    ? null
    : "Invalid Telegram bot token format.";
};

export const TelegramConfig = ({ id }: { id: string }) => {
  const [newBotTelegramToken, setNewTelegramToken] = useState<string>(
    "",
  );
  const token = useAuth().user?.refresh_token;
  const [tokenError, setTokenError] = useState<string | null>(null);
  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } } },
  });
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  const { bots } = data;
  const [bot] = bots;
  const { telegramToken, telegramUsername } = bot || {};
  return (
    <Collapsible title="Telegram">
      <div className="flex flex-col md:flex-row md:items-center gap-2 w-full">
        {!telegramToken
          ? (
            <div className="mb-4 w-full">
              <label>
                Telegram bot token
              </label>
              <input
                value={newBotTelegramToken}
                onChange={(e) => {
                  setNewTelegramToken(e.currentTarget.value);
                  setTokenError(validateToken(e.currentTarget.value));
                }}
                className={inputClass}
              />
              <Button
                onClick={() => {
                  if (!token) return;
                  toast.promise(
                    apiClient({
                      token,
                      endpoint: "set-telegram-token",
                      payload: {
                        telegramToken: newBotTelegramToken,
                        botId: bot.id,
                      },
                    }),
                    {
                      loading: "Connecting bot to Telegram...",
                      success: (res) => {
                        if (res.success) {
                          setNewTelegramToken("");
                          setTokenError(null);
                          return "Bot connected successfully!";
                        }
                        throw new Error(res.error);
                      },
                      error: (err) => {
                        setTokenError(err.message);
                        return `Failed to connect bot: ${err.message}`;
                      },
                    },
                  );
                }}
                disabled={!newBotTelegramToken ||
                  !!validateToken(newBotTelegramToken)}
              >
                Connect to Telegram
              </Button>
              {tokenError && (
                <div className="text-danger text-sm mt-1">{tokenError}</div>
              )}
              <div>
                To get a token, go to{" "}
                <a
                  href="https://t.me/botfather"
                  className="text-accent-light dark:text-accent underline"
                >
                  BotFather
                </a>{" "}
                and write <code className={inlineCodeClass}>/newbot</code>{" "}
                to get a token.
              </div>
            </div>
          )
          : (
            <div className="flex flex-col md:flex-row md:items-center gap-2 w-full">
              <TokenDisplay
                label="Token"
                token={telegramToken}
              />
              {telegramUsername && (
                <Button
                  onClick={() => {
                    globalThis.open(
                      `https://t.me/${telegramUsername}`,
                      "_blank",
                    );
                  }}
                  className="w-full md:w-auto"
                >
                  Talk on Telegram
                </Button>
              )}
              <Button
                onClick={() => {
                  if (!token) return;
                  toast.promise(
                    apiClient({
                      token,
                      endpoint: "disconnect-telegram",
                      payload: { botId: bot.id },
                    }).then((res) => {
                      if (!res.success) {
                        throw new Error(res.error ?? "Unknown error");
                      }
                    }),
                    {
                      loading: "Disconnecting Telegram...",
                      success: "Telegram bot disconnected.",
                      error: (err) => `Failed to disconnect: ${err.message}`,
                    },
                  );
                }}
                className="w-full md:w-auto bg-danger hover:bg-danger-hover"
              >
                Disconnect
              </Button>
            </div>
          )}
      </div>
    </Collapsible>
  );
};
